import { useEffect, useRef } from "react";

// Requires: npm install chart.js
// Usage: <Topline360MobileDashboard />

const MONTHS = ["Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar","Apr","May","Jun"];
const SALES_DATA  = [0.82,0.95,1.08,1.25,1.45,1.62,1.70,1.88,2.05,2.30,2.60,2.90];
const COLLS_DATA  = [0.78,0.88,1.00,1.18,1.35,1.52,1.60,1.75,1.92,2.10,2.30,2.55];

const KPI_CARDS = [
  {
    label: "Sales MTD",
    value: "AED 2.85M",
    delta: "↑ 12% vs target",
    trend: "up",
    color: "blue",
    icon: "📈",
  },
  {
    label: "Collection",
    value: "AED 2.12M",
    delta: "↑ 8% vs last month",
    trend: "up",
    color: "green",
    icon: "💼",
  },
  {
    label: "Gross Profit",
    value: "AED 645K",
    delta: "↑ 5% · margin 22.6%",
    trend: "up",
    color: "amber",
    icon: "%",
  },
  {
    label: "Receivables",
    value: "AED 1.32M",
    delta: "↓ 4% vs last MTD",
    trend: "down",
    color: "red",
    icon: "⚠",
  },
];

const LIQUIDITY_CARDS = [
  { label: "Cash",        value: "AED 76K" },
  { label: "Bank",        value: "AED 1.10M" },
  { label: "PDC In Hand", value: "AED 420K" },
  { label: "PDC Issued",  value: "AED 350K" },
];

const NAV_ITEMS = [
  { label: "Dashboard", icon: "⊞", active: true },
  { label: "CRM",       icon: "👥" },
  { label: "Sales",     icon: "$" },
  { label: "Inventory", icon: "📦" },
  { label: "Reports",   icon: "📊" },
];

// ── Color maps ──────────────────────────────────────────────────────────────
const borderColor = {
  blue:  "#2563eb",
  green: "#16a34a",
  amber: "#d97706",
  red:   "#dc2626",
};
const iconBg = {
  blue:  { background: "#eff6ff", color: "#2563eb" },
  green: { background: "#f0fdf4", color: "#16a34a" },
  amber: { background: "#fffbeb", color: "#d97706" },
  red:   { background: "#fef2f2", color: "#dc2626" },
};
const deltaColor = { up: "#16a34a", down: "#dc2626" };

// ── Styles (plain objects — no Tailwind required) ───────────────────────────
const s = {
  shell: {
    width: "100%",
    maxWidth: 390,
    margin: "0 auto",
    background: "#f0f2f5",
    borderRadius: 28,
    overflow: "hidden",
    fontFamily: "system-ui, -apple-system, sans-serif",
    minHeight: 860,
    paddingBottom: 0,
  },
  // Top bar
  topbar: {
    background: "#111827",
    padding: "16px 18px 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  logoArea: { display: "flex", alignItems: "center", gap: 9 },
  logoTile: {
    width: 30, height: 30, borderRadius: 7,
    background: "#2563eb",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 13, fontWeight: 700, color: "#fff",
  },
  logoName: { fontSize: 15, fontWeight: 600, color: "#fff", letterSpacing: -0.2 },
  topbarRight: { display: "flex", alignItems: "center", gap: 10 },
  iconBtn: {
    width: 32, height: 32, borderRadius: "50%",
    background: "rgba(255,255,255,0.08)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 15, color: "rgba(255,255,255,0.55)",
    border: "none", cursor: "pointer", position: "relative",
  },
  notifDot: {
    position: "absolute", top: 5, right: 5,
    width: 7, height: 7, borderRadius: "50%",
    background: "#ef4444", border: "1.5px solid #111827",
  },
  userPill: {
    display: "flex", alignItems: "center", gap: 7,
    background: "rgba(255,255,255,0.08)", borderRadius: 20,
    padding: "4px 10px 4px 4px",
  },
  userAv: {
    width: 24, height: 24, borderRadius: "50%",
    background: "#2563eb",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 11, fontWeight: 600, color: "#fff",
  },
  userName: { fontSize: 12, color: "rgba(255,255,255,0.8)", fontWeight: 500 },
  // Page head
  pageHead: {
    background: "#fff", padding: "14px 18px 12px",
    borderBottom: "0.5px solid #e5e7eb",
    display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  pageTitle: { fontSize: 15, fontWeight: 600, color: "#111827" },
  pageSub:   { fontSize: 11, color: "#9ca3af", marginTop: 2 },
  erpBadge: {
    display: "flex", alignItems: "center", gap: 5,
    fontSize: 10, color: "#16a34a", fontWeight: 500,
    background: "#f0fdf4", border: "0.5px solid #bbf7d0",
    padding: "4px 9px", borderRadius: 20,
  },
  erpDot: { width: 6, height: 6, borderRadius: "50%", background: "#16a34a" },
  // Section label
  secLabel: {
    padding: "14px 18px 8px",
    fontSize: 10, fontWeight: 600,
    letterSpacing: "0.09em", textTransform: "uppercase", color: "#6b7280",
  },
  // KPI grid
  kpiGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr",
    gap: 10, padding: "0 14px",
  },
  kpiCard: (color) => ({
    background: "#fff", borderRadius: 12,
    border: "0.5px solid #e5e7eb",
    borderLeft: `3px solid ${borderColor[color]}`,
    padding: "13px 13px 11px",
  }),
  kpiTop: {
    display: "flex", alignItems: "flex-start",
    justifyContent: "space-between", marginBottom: 8,
  },
  kpiLbl: {
    fontSize: 9.5, fontWeight: 600,
    letterSpacing: "0.07em", textTransform: "uppercase", color: "#9ca3af",
  },
  kpiIco: (color) => ({
    width: 26, height: 26, borderRadius: 7,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 13, ...iconBg[color],
  }),
  kpiVal: {
    fontSize: 17, fontWeight: 700, color: "#111827",
    letterSpacing: -0.4, lineHeight: 1, marginBottom: 5,
  },
  kpiDelta: (trend) => ({
    fontSize: 10, fontWeight: 500, color: deltaColor[trend],
  }),
  // Liquidity
  secGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr",
    gap: 10, padding: "10px 14px 4px",
  },
  secCard: {
    background: "#fff", borderRadius: 12,
    border: "0.5px solid #e5e7eb", padding: "12px 13px",
  },
  secLbl: {
    fontSize: 9.5, fontWeight: 600,
    letterSpacing: "0.07em", textTransform: "uppercase",
    color: "#9ca3af", marginBottom: 6,
  },
  secVal: { fontSize: 15, fontWeight: 700, color: "#111827", letterSpacing: -0.3 },
  // Chart
  chartWrap: {
    margin: "14px 14px 0",
    background: "#fff", borderRadius: 14,
    border: "0.5px solid #e5e7eb", padding: "16px 14px 14px",
  },
  chartTtl: { fontSize: 13, fontWeight: 600, color: "#111827" },
  chartSub: { fontSize: 11, color: "#9ca3af", marginTop: 2, marginBottom: 12 },
  legend: { display: "flex", gap: 14, marginBottom: 12 },
  legItem: { display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: "#6b7280" },
  legDot: (color) => ({ width: 8, height: 8, borderRadius: 2, background: color }),
  // Bottom nav
  bottomNav: {
    display: "flex", justifyContent: "space-around",
    padding: "12px 10px 16px",
    background: "#111827", marginTop: 16,
  },
  navItem: (active) => ({
    display: "flex", flexDirection: "column", alignItems: "center",
    gap: 4, fontSize: 9.5,
    color: active ? "#60a5fa" : "rgba(255,255,255,0.35)",
    cursor: "pointer", minWidth: 44,
  }),
  navIconWrap: (active) => ({
    background: active ? "rgba(37,99,235,0.18)" : "transparent",
    borderRadius: 10, padding: active ? "4px 10px" : "4px 10px",
  }),
  navIcon: { fontSize: 19 },
};

// ── Sub-components ───────────────────────────────────────────────────────────

function TopBar() {
  return (
    <div style={s.topbar}>
      <div style={s.logoArea}>
        <div style={s.logoTile}>T</div>
        <span style={s.logoName}>Topline360</span>
      </div>
      <div style={s.topbarRight}>
        
        <button style={{ ...s.iconBtn }} aria-label="Notifications">
          🔔
          <div style={s.notifDot} />
        </button>
        <div style={s.userPill}>
          <div style={s.userAv}>R</div>
          <span style={s.userName}>Raj</span>
        </div>
      </div>
    </div>
  );
}

function PageHead() {
  return (
    <div style={s.pageHead}>
      <div>
        <div style={s.pageTitle}>Dashboard</div>
        <div style={s.pageSub}>Topline360 ERP · Executive Management</div>
      </div>
      <div style={s.erpBadge}>
        <div style={s.erpDot} />
        ERP Live Sync
      </div>
    </div>
  );
}

function KpiCard({ label, value, delta, trend, color, icon }) {
  return (
    <div style={s.kpiCard(color)}>
      <div style={s.kpiTop}>
        <div style={s.kpiLbl}>{label}</div>
        <div style={s.kpiIco(color)}>{icon}</div>
      </div>
      <div style={s.kpiVal}>{value}</div>
      <div style={s.kpiDelta(trend)}>{delta}</div>
    </div>
  );
}

function LiquidityCard({ label, value }) {
  return (
    <div style={s.secCard}>
      <div style={s.secLbl}>{label}</div>
      <div style={s.secVal}>{value}</div>
    </div>
  );
}

function TrendChart() {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  useEffect(() => {
    // Dynamically import Chart.js to avoid SSR issues
    import("chart.js/auto").then(({ default: Chart }) => {
      if (chartRef.current) chartRef.current.destroy();
      chartRef.current = new Chart(canvasRef.current, {
        type: "line",
        data: {
          labels: MONTHS,
          datasets: [
            {
              label: "Sales",
              data: SALES_DATA,
              borderColor: "#2563eb",
              backgroundColor: "rgba(37,99,235,0.08)",
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              tension: 0.45,
              fill: true,
            },
            {
              label: "Collections",
              data: COLLS_DATA,
              borderColor: "#16a34a",
              backgroundColor: "rgba(22,163,74,0.07)",
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              tension: 0.45,
              fill: true,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: "index",
              intersect: false,
              callbacks: {
                label: (ctx) => ` AED ${ctx.parsed.y.toFixed(2)}M`,
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { font: { size: 9 }, color: "#9ca3af", maxRotation: 0 },
            },
            y: {
              grid: { color: "#f3f4f6", lineWidth: 1 },
              ticks: {
                font: { size: 9 },
                color: "#9ca3af",
                callback: (v) => `${v.toFixed(1)}M`,
              },
              min: 0,
              max: 3.2,
            },
          },
        },
      });
    });

    return () => {
      if (chartRef.current) chartRef.current.destroy();
    };
  }, []);

  return (
    <div style={s.chartWrap}>
      <div style={s.chartTtl}>Sales Trend (Last 12 Months)</div>
      <div style={s.chartSub}>Dynamic billing &amp; collection cycles</div>
      <div style={s.legend}>
        <div style={s.legItem}>
          <div style={s.legDot("#2563eb")} />
          Sales (AED)
        </div>
        <div style={s.legItem}>
          <div style={s.legDot("#16a34a")} />
          Collections (AED)
        </div>
      </div>
      <div style={{ position: "relative", width: "100%", height: 160 }}>
        <canvas
          ref={canvasRef}
          role="img"
          aria-label="Line chart: Sales and Collections rising from 0.8M in Jul to 2.9M in Jun"
        />
      </div>
    </div>
  );
}

function BottomNav() {
  return (
    <nav style={s.bottomNav} aria-label="Main navigation">
      {NAV_ITEMS.map(({ label, icon, active }) => (
        <div key={label} style={s.navItem(active)}>
          <div style={s.navIconWrap(active)}>
            <span style={s.navIcon}>{icon}</span>
          </div>
          <span>{label}</span>
        </div>
      ))}
    </nav>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function HomeNew2() {
  return (
    <div style={s.shell}>
      <TopBar />
      <PageHead />

      <div style={s.secLabel}>Business Health</div>
      <div style={s.kpiGrid}>
        {KPI_CARDS.map((card) => (
          <KpiCard key={card.label} {...card} />
        ))}
      </div>

      <div style={{ ...s.secLabel, paddingTop: 10 }}>Liquidity</div>
      <div style={s.secGrid}>
        {LIQUIDITY_CARDS.map((card) => (
          <LiquidityCard key={card.label} {...card} />
        ))}
      </div>

      <TrendChart />
      <BottomNav />
    </div>
  );
}